import { Button, Icon } from '@vinted/web-ui';
import { Stats16 } from '@vinted/monochrome-icons';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'components/@support';
import ItemGrid from 'components/ItemGrid';
import { ProductItem } from 'components/ProductItem';
import ProfileItemPushUpButton from 'components/ProfileItemPushUpButton';
import { addClickListItemTracking } from 'containers/TrackingProvider';
import useTracking from 'hooks/useTracking';
import { ItemAlertStatus } from 'constants/item';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { ContentSource } from 'constants/tracking/content-sources';
import { ListItemContentType } from 'constants/tracking/content-types';
import { EDIT_ITEM_URL, ITEM_URL } from 'constants/routes';
import { getItemStatus } from 'data/utils/item';
import { clickEvent, impressionEvent } from 'libs/common/event-tracker/events';
import { transformItemDtoToProductItem } from 'data/transformers/product-item';
import { getVasGalleryAbTest } from 'state/vas-gallery/selectors';
import { logClosetItemError } from 'pages/Profile/utils/log';
import { jsx as _jsx } from "react/jsx-runtime";
const TrackedProductItem = addClickListItemTracking(ProductItem);

const ItemsContainer = _ref => {
  let {
    items,
    userId,
    allowBump,
    isViewingSelf
  } = _ref;
  const {
    track
  } = useTracking();
  const vasGalleryAbTest = useSelector(getVasGalleryAbTest);

  const handleItemVisibility = _ref2 => {
    let {
      item: {
        data,
        id
      },
      index
    } = _ref2;
    const event = impressionEvent({
      id,
      position: index + 1,
      contentType: ListItemContentType.Item,
      contentSource: ContentSource.UserItems,
      itemOwnerId: data.user_id || userId
    });
    track(event);
  };

  const handleErrorLogging = err => logClosetItemError(err);

  const onActionClick = (target, itemId) => () => {
    const trackingEvent = clickEvent({
      target,
      targetDetails: itemId.toString()
    });
    track(trackingEvent);
  };

  const renderPerformanceChartButton = id => /*#__PURE__*/_jsx(Button, {
    text: /*#__PURE__*/_jsx(Icon, {
      name: Stats16,
      color: "parent"
    }),
    theme: "primary",
    testId: "bump-performance-button",
    size: Button.Size.Small,
    url: ITEM_URL(id),
    onClick: onActionClick(ClickableElement.ShowItemPerformance, id)
  });

  const resolveItemWidth = () => 'one-fifth';

  const renderItemFooter = item => {
    const {
      id,
      promoted,
      vas_gallery_promoted
    } = item;
    if (!isViewingSelf) return null;
    const status = getItemStatus(item, isViewingSelf);

    switch (status) {
      case ItemAlertStatus.ReplicaProof:
      case ItemAlertStatus.UnderReview:
        return /*#__PURE__*/_jsx(Button, {
          text: /*#__PURE__*/_jsx(FormattedMessage, {
            id: "item.actions.edit"
          }),
          theme: "primary",
          size: Button.Size.Small,
          url: EDIT_ITEM_URL(id)
        });

      case 'promoted':
        {
          if (!promoted && allowBump) return /*#__PURE__*/_jsx(ProfileItemPushUpButton, {
            item: item
          });
          return renderPerformanceChartButton(id);
        }

      case 'bumped':
        {
          if (vasGalleryAbTest.visible && !vas_gallery_promoted) return /*#__PURE__*/_jsx(ProfileItemPushUpButton, {
            item: item
          });
          return renderPerformanceChartButton(id);
        }

      case 'draft':
        {
          return /*#__PURE__*/_jsx(Button, {
            text: /*#__PURE__*/_jsx(FormattedMessage, {
              id: "item.drafts.finish_editing"
            }),
            theme: "primary",
            size: Button.Size.Small,
            url: EDIT_ITEM_URL(id)
          });
        }

      default:
        {
          if (!allowBump) return null;
          return /*#__PURE__*/_jsx(ProfileItemPushUpButton, {
            item: item
          });
        }
    }
  };

  const renderItem = _ref3 => {
    let {
      item: {
        data
      },
      index
    } = _ref3;
    return /*#__PURE__*/_jsx(TrackedProductItem, {
      showOwner: false,
      item: transformItemDtoToProductItem(data),
      renderFooter: renderItemFooter(data),
      showStatus: true,
      viewingSelf: isViewingSelf,
      tracking: {
        id: data.id,
        contentType: ListItemContentType.Item,
        contentSource: ContentSource.UserItems,
        position: index + 1
      },
      url: data.is_draft ? EDIT_ITEM_URL(data.id) : undefined,
      hideBusinessBadge: true
    });
  };

  return /*#__PURE__*/_jsx(ItemGrid, {
    items: items.map(item => ({
      id: item.id,
      data: item
    })),
    renderItem: renderItem,
    onItemVisible: handleItemVisibility,
    widthResolver: resolveItemWidth,
    preventLog: true,
    handleErrorLogging: handleErrorLogging
  });
};

export default ItemsContainer;